import React from 'react';
import { Campaign } from '../../types/Campaign';
import { Badge } from '../atoms/Badge';
import { ProgressBar } from '../atoms/ProgressBar';

interface CampaignCardProps {
  campaign: Campaign;
}

export const CampaignCard: React.FC<CampaignCardProps> = ({ campaign }) => {
  const budgetPercentage = (campaign.consumedBudget / campaign.totalBudget) * 100;
  const rejectionPercentage = campaign.totalPosts > 0
    ? (campaign.totalRejectedPosts / campaign.totalPosts) * 100
    : 0;

  const getRejectionStatusColor = (percentage: number) => {
    if (percentage < 5) return 'text-emerald-600 dark:text-emerald-400';
    if (percentage <= 10) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const handleClick = () => {
    window.open(campaign.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      onClick={handleClick}
      className={`bg-white/80 dark:bg-dark-300/80 backdrop-blur-sm rounded-3xl p-6 hover:shadow-soft-lg transition-all duration-300 cursor-pointer grid grid-cols-[90px_1fr_110px_110px_220px_110px_90px_110px] gap-6 items-center border-0 shadow-soft ${
        campaign.status === 'done' ? 'opacity-50' : ''
      }`}
    >
      <div className="flex justify-center">
        <Badge status={campaign.status} />
      </div>

      <div className="min-w-0">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 truncate mb-1">
          {campaign.campaignTitle}
        </h3>
        <div className="flex items-center gap-2">
          <p className="text-sm text-gray-500 dark:text-gray-400">{campaign.brandName}</p>
          {campaign.isRecurrent && (
            <span className="text-xs bg-purple-50 dark:bg-dark-purple-400/30 text-purple-700 dark:text-dark-purple-100 px-2.5 py-1 rounded-full whitespace-nowrap font-medium">
              Recorrente
            </span>
          )}
        </div>
      </div>

      <div className="text-left text-sm text-gray-700 dark:text-gray-300 font-medium">{formatDate(campaign.startDate)}</div>

      <div className="text-left text-sm text-gray-700 dark:text-gray-300 font-medium">{formatDate(campaign.endDate)}</div>

      <div>
        <div className="text-sm text-gray-700 dark:text-gray-300 font-medium mb-2">
          {formatCurrency(campaign.consumedBudget)} / {formatCurrency(campaign.totalBudget)}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 flex items-center">
            <ProgressBar percentage={budgetPercentage} showLabel={false} />
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 font-semibold min-w-[32px] text-right flex items-center justify-end">
            {budgetPercentage.toFixed(0)}%
          </div>
        </div>
      </div>

      <div className="text-center text-sm text-gray-700 dark:text-gray-300 font-medium">
        {campaign.creatorsWithPosts}/{campaign.creatorsRegistered}
      </div>

      <div className="text-center text-sm text-gray-700 dark:text-gray-300 font-medium">{campaign.totalPosts}</div>

      <div className="text-center text-sm font-medium">
        <span className="text-gray-700 dark:text-gray-300">{campaign.totalRejectedPosts}</span>
        <span className={`ml-1 ${getRejectionStatusColor(rejectionPercentage)}`}>
          ({rejectionPercentage.toFixed(0)}%)
        </span>
      </div>
    </div>
  );
};
